import { API } from '@/lib/api';

export const REVIEW_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_follow_up', label: 'Needs follow-up' },
];

const REVIEW_ALIASES = {
  machine: 'new',
  auto: 'new',
  pending: 'new',
  draft: 'new',
  needs_review: 'new',
  reviewed: 'reviewed',
  accepted: 'accepted',
  approved: 'accepted',
  rejected: 'rejected',
  discarded: 'rejected',
  do_not_keep: 'rejected',
  needs_follow_up: 'needs_follow_up',
};

export function normalizeReviewState(value, fallback = 'new') {
  const raw = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return REVIEW_ALIASES[raw] || fallback;
}

export function reviewMeta(value) {
  const normalized = normalizeReviewState(value);
  if (normalized === 'accepted') return { label: 'Accepted', bg: 'rgba(34,197,94,0.14)', color: '#15803d' };
  if (normalized === 'rejected') return { label: 'Rejected', bg: 'rgba(239,68,68,0.14)', color: '#b91c1c' };
  if (normalized === 'reviewed') return { label: 'Reviewed', bg: 'rgba(14,165,233,0.14)', color: '#0369a1' };
  if (normalized === 'needs_follow_up') return { label: 'Needs follow-up', bg: 'rgba(245,158,11,0.16)', color: '#b45309' };
  return { label: 'New', bg: 'rgba(148,163,184,0.14)', color: 'var(--text-secondary)' };
}

export function buildCollaborationState(initialState = {}, fallbackReviewState = 'new') {
  const reviewState = normalizeReviewState(
    initialState?.review_state
      || initialState?.reviewState
      || initialState?.collaborationReviewState
      || fallbackReviewState,
    normalizeReviewState(fallbackReviewState),
  );
  return {
    review_state: reviewState,
    owner: initialState?.owner || null,
    comments: initialState?.comments || [],
    comments_count: initialState?.comments_count || (initialState?.comments || []).length || 0,
    review_note: initialState?.review_note || null,
    saved_decision: initialState?.saved_decision || null,
    updated_at: initialState?.updated_at || '',
    target_label: initialState?.target_label || '',
  };
}

export async function fetchCollaborationState({ matterId, targetType, targetId, headers }) {
  const response = await fetch(`${API}/matters/${matterId}/collaboration-states/${targetType}/${targetId}`, {
    credentials: 'include',
    headers,
  });
  if (!response.ok) throw new Error('Unable to load collaboration state');
  const payload = await response.json();
  return buildCollaborationState(payload?.state || {});
}

export async function updateCollaborationState({ matterId, targetType, targetId, body, headers }) {
  const response = await fetch(`${API}/matters/${matterId}/collaboration-states/${targetType}/${targetId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('Unable to save collaboration state');
  const payload = await response.json();
  return buildCollaborationState(payload?.state || {});
}

export async function addCollaborationComment({ matterId, targetType, targetId, text, headers }) {
  const response = await fetch(`${API}/matters/${matterId}/collaboration-states/${targetType}/${targetId}/comments`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ text }),
  });
  if (!response.ok) throw new Error('Unable to save note');
  const payload = await response.json();
  return buildCollaborationState(payload?.state || {});
}
